import React, { useEffect, useState } from "react";
import YhModal from "../../../../ui/YhModal";
import YhButton from "../../../../ui/YhButton";
import YhInput from "../../../../ui/YhInput";
import {
  getProjectDetails,
  updateProjectDetail,
} from "../stepper/StepperService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";

type PriceRow = {
  key: string;
  bedroom: string;
  propertyType: string;
  carpetArea: string;
  price: string;
};

type UpdatePricingModalProps = {
  open: boolean;
  projectId: string;
  projectName?: string;
  onClose: () => void;
  onSaved?: () => void;
};

const unitLabel = (row: PriceRow) =>
  [row.bedroom, row.propertyType].filter(Boolean).join(" · ") || "Unit";

const startingPrice = (rows: PriceRow[]) => {
  const nums = rows
    .map((row) => Number(String(row.price || "").replace(/,/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (nums.length) return String(Math.min(...nums));
  return String(rows[0]?.price || "").trim();
};

const UpdatePricingModal = ({
  open,
  projectId,
  projectName,
  onClose,
  onSaved,
}: UpdatePricingModalProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [rawList, setRawList] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setRows([]);
      setRawList([]);
      try {
        const response = await getProjectDetails(projectId);
        if (cancelled) return;
        const data = response?.data;
        const list = Array.isArray(data?.priceList) ? data.priceList : [];
        setRawList(list);
        setRows(
          list.map((item: any, index: number) => ({
            key: String(item?._id || index),
            bedroom: String(item?.bedroom || "").trim(),
            propertyType: String(item?.propertyType || "").trim(),
            carpetArea:
              item?.carpetArea == null || item?.carpetArea === ""
                ? ""
                : String(item.carpetArea),
            price:
              item?.price == null || item?.price === ""
                ? ""
                : String(item.price),
          }))
        );
      } catch (error: any) {
        if (!cancelled) {
          openErrorNotification(
            error?.message || "Failed to load unit prices"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const handlePriceChange = (index: number, value: string) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, price: value } : row))
    );
  };

  const handleSave = async () => {
    if (!projectId) return;
    try {
      setSaving(true);
      const priceList = rawList.map((item: any, index: number) => ({
        ...item,
        price: rows[index]?.price ?? item?.price ?? "",
      }));
      const response = await updateProjectDetail({
        _id: projectId,
        priceList,
        price: startingPrice(rows),
      });
      if (response?.success) {
        openSuccessNotification(
          response?.message || "Pricing updated. Website will show the new prices."
        );
        onSaved?.();
        onClose();
      } else {
        openErrorNotification(response?.message || "Failed to update pricing");
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to update pricing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <YhModal
      open={open}
      onClose={onClose}
      width={640}
      title={
        <div>
          <div className="text-lg font-bold text-[#111827]">Update Pricing</div>
          <p className="mt-0.5 text-sm font-normal text-[#6B7280]">
            {projectName || "Project"} — change unit prices and save. Website
            updates from this list.
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <YhButton variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            disabled={saving || loading || !rows.length}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save prices"}
          </YhButton>
        </div>
      }
    >
      {loading ? (
        <div className="flex min-h-[180px] flex-col items-center justify-center gap-2">
          <span className="inline-block h-8 w-8 animate-spin rounded-full border-[3px] border-[#E8D0D8] border-t-[#A02448]" />
          <p className="text-sm text-[#6B7280]">Loading unit prices…</p>
        </div>
      ) : !rows.length ? (
        <div className="rounded-xl border border-[#EEF0F5] bg-[#FCFCFD] px-4 py-8 text-center text-sm text-[#6B7280]">
          No unit config found. Add units from Edit Project first.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#EEF0F5]">
          <div className="grid grid-cols-[minmax(0,1.4fr)_90px_minmax(0,1fr)] gap-2 bg-[#F8FAFC] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            <span>Unit</span>
            <span>Area</span>
            <span>Price</span>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {rows.map((row, index) => (
              <div
                key={row.key}
                className="grid grid-cols-[minmax(0,1.4fr)_90px_minmax(0,1fr)] items-center gap-2 border-t border-[#EEF0F5] px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#111827]">
                    {unitLabel(row)}
                  </p>
                </div>
                <p className="text-sm text-[#6B7280]">
                  {row.carpetArea ? `${row.carpetArea} sqft` : "—"}
                </p>
                <YhInput
                  value={row.price}
                  placeholder="Enter price"
                  onChange={(e) => handlePriceChange(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </YhModal>
  );
};

export default UpdatePricingModal;
